import { Box } from "@mui/material"
import { useSelector } from "@ggs/store"
import { IconButton, SvgIcon } from ".."
import type { IconButtonProps } from "../Button/IconButton"
import { useEffect, useState } from "react"

type NavigationFavoritesProps = Omit<IconButtonProps, "icon"> & {
  showCount?: boolean
  size?: number
}

const NavigationFavorites = ({
  showCount = true,
  size = 18,
  sx,
  ...props
}: NavigationFavoritesProps) => {
  // Get wishlist data from Redux store
  const wishlist = useSelector((state) => state.views.wishlist)

  // Track previous state to detect changes
  const [prevHasFavorites, setPrevHasFavorites] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)

  // Calculate total favorited items
  const totalFavorited = Object.values(wishlist || {}).reduce(
    (total: number, items: any[] | unknown) => total + ((items as any[])?.length || 0),
    0
  )

  const hasFavorites = totalFavorited > 0

  // Detect changes to trigger animation
  useEffect(() => {
    if (hasFavorites !== prevHasFavorites) {
      setShouldAnimate(true)
      const timer = setTimeout(() => setShouldAnimate(false), 1000)
      setPrevHasFavorites(hasFavorites)
      return () => clearTimeout(timer)
    }
  }, [hasFavorites, prevHasFavorites])

  return (
    <Box position="relative">
      <IconButton
        variant="base"
        aria-label="Wishlist"
        icon={
          <SvgIcon
            variant="heart"
            strokeMode
            size={size}
            sx={(theme) => ({
              "@keyframes pulseHeart": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.2)" },
                "100%": { transform: "scale(1)" },
              },
              "@keyframes popInCounter": {
                "0%": { transform: "scale(0)", opacity: 0 },
                "70%": { transform: "scale(1.2)", opacity: 1 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
              "strokeWidth": 2,
              // Change stroke color if there are favorites
              "stroke": hasFavorites
                ? theme.palette["primary.800"].main
                : theme.palette["neutral.700"].main,
              // Change fill color if there are favorites
              "fill": hasFavorites ? theme.palette["primary.800"].main : "none",
              // Add animation when state changes
              "animation": shouldAnimate ? "pulseHeart 300ms ease-in-out forwards" : "none",
            })}
          />
        }
        sx={[
          (theme) => ({
            height: { xs: "auto" },
            width: { xs: "auto" },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        href="/wishlist"
        target="_self"
        {...props}
      />

      {/* Count indicator */}
      {showCount && hasFavorites && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: -8,
            right: -8,
            backgroundColor: theme.palette["primary.900"].main,
            color: theme.palette["neutral.100"].main,
            borderRadius: "50%",
            width: 16,
            height: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: "bold",
            // Add animation for counter appearance
            animation: shouldAnimate ? "popInCounter 300ms ease-in-out forwards" : "none",
          })}
        >
          {totalFavorited}
        </Box>
      )}
    </Box>
  )
}

export default NavigationFavorites
